var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * CZNA-407 — edit the wording of one help text.
 *
 * Every save writes the whole `help_text.overrides` map through the ordinary module-settings
 * PUT, built from the row as the server last returned it — never from the provider's
 * filtered copy, which would silently drop entries it could not use.
 *
 * "Vrátit výchozí" is that same PUT with the key removed. A DELETE would try to reset the
 * row to a packaged default, and this row has none (409). Saving text identical to the
 * packaged one removes the key too, so "Upraveno" never marks a text that is not.
 */
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, KeyValue, MessageBanner, Pane, PaneFooter, TextArea, } from '@folio/stripes/components';
import useSaveModuleSetting from '../hooks/useSaveModuleSetting';
import { overrideProblem } from '../utils/helpTextOverrides';
export var HELP_TEXT_SETTING_KEY = 'help_text.overrides';
var PLACEHOLDER = /\{\s*([^\s,{}]+)\s*[,}]/g;
/** `{name}` placeholders of the packaged text, shown so NK knows to keep them. */
var placeholdersOf = function (text) { return (Array.from(new Set(Array.from(text.matchAll(PLACEHOLDER), function (m) { return "{".concat(m[1], "}"); })))); };
var HelpTextDetail = function (_a) {
    var row = _a.row, title = _a.title, storedValue = _a.storedValue, optimisticLock = _a.optimisticLock, canWrite = _a.canWrite, onClose = _a.onClose, onSaved = _a.onSaved;
    var intl = useIntl();
    var save = useSaveModuleSetting(HELP_TEXT_SETTING_KEY);
    var storedIsUsable = row.stored !== undefined
        && overrideProblem(row.stored, row.defaultText, intl) === null;
    var _b = useState(storedIsUsable ? row.stored : row.defaultText), draft = _b[0], setDraft = _b[1];
    var problem = overrideProblem(draft, row.defaultText, intl);
    var placeholders = useMemo(function () { return placeholdersOf(row.defaultText); }, [row.defaultText]);
    var ready = storedValue !== null && optimisticLock !== undefined;
    var write = function (text, outcome) {
        if (!ready)
            return;
        var next = __assign({}, storedValue);
        if (text === null || text.trim() === row.defaultText.trim()) {
            delete next[row.id];
        }
        else {
            next[row.id] = text;
        }
        save.mutate({ value: next, optimisticLock: optimisticLock }, { onSuccess: function () { return onSaved(outcome); } });
    };
    var saveErrorId = function () {
        // CZNA-1625 — no answer in time: the overrides may have been written.
        if (save.timedOut)
            return 'ui-czna-authorities.error.timeout';
        return save.errorStatus === 409
            ? 'ui-czna-authorities.guidanceTexts.error.conflict'
            : 'ui-czna-authorities.guidanceTexts.error.save';
    };
    var footer = canWrite ? (React.createElement(PaneFooter, { renderEnd: (React.createElement(React.Fragment, null,
            row.stored !== undefined && (React.createElement(Button, { marginBottom0: true, disabled: !ready || save.isLoading, onClick: function () { return write(null, 'reset'); } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.guidanceTexts.detail.reset" }))),
            React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, disabled: !ready || save.isLoading || problem !== null, onClick: function () { return write(draft, 'saved'); } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.guidanceTexts.detail.save" })))) })) : undefined;
    return (React.createElement(Pane, { id: "guidance-text-detail-pane", defaultWidth: "40%", paneTitle: title, dismissible: true, onClose: onClose, footer: footer },
        save.isError && (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: saveErrorId() }))),
        row.stored !== undefined && !storedIsUsable && (React.createElement(MessageBanner, { type: "warning" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.guidanceTexts.detail.storedUnusable" }))),
        React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.guidanceTexts.detail.defaultText" }), value: React.createElement("span", { style: { whiteSpace: 'pre-wrap' } }, row.defaultText) }),
        canWrite ? (React.createElement(TextArea, { name: "guidance-text", label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.guidanceTexts.detail.text" }), value: draft, onChange: function (e) { return setDraft(e.target.value); }, error: problem
                ? intl.formatMessage({ id: "ui-czna-authorities.guidanceTexts.problem.".concat(problem) })
                : undefined })) : (React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.guidanceTexts.detail.text" }), value: React.createElement("span", { style: { whiteSpace: 'pre-wrap' } }, draft) })),
        canWrite && placeholders.length > 0 && (React.createElement("p", null,
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.guidanceTexts.detail.placeholders", values: { names: placeholders.join(', ') } })))));
};
export default HelpTextDetail;
